import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { 
  FaShieldAlt, FaServer, FaDollarSign, 
  FaExclamationTriangle, FaCheckCircle, FaChartLine, FaGlobe 
} from 'react-icons/fa';
import Chart from 'react-apexcharts';

// Helper function for region coordinates - CALIBRATED Feb 5, 2026
// Based on user calibration:
// us-east-1: 25.5%, 53.3%
// eu-central-1: 50.4%, 44.3%
// ap-southeast-2: 88.5%, 84.3%
// me-central-1: 61.5%, 59.5%
const getRegionCoords = (region) => {
  const coords = {
    // US Regions
    'us-east-1': { x: 25.5, y: 53.3 },
    'us-east-2': { x: 26.8, y: 51.8 },
    'us-west-1': { x: 13.8, y: 54.5 },
    'us-west-2': { x: 12.2, y: 50.5 },
    // Canada
    'ca-central-1': { x: 26.2, y: 47.5 },
    'ca-west-1': { x: 13.2, y: 47.0 },
    // Europe
    'eu-west-1': { x: 46.8, y: 45.5 },
    'eu-west-2': { x: 48.2, y: 46.0 },
    'eu-west-3': { x: 49.8, y: 48.5 },
    'eu-central-1': { x: 50.4, y: 44.3 },
    'eu-central-2': { x: 51.8, y: 49.0 },
    'eu-north-1': { x: 51.5, y: 38.5 },
    'eu-south-1': { x: 51.2, y: 51.5 },
    'eu-south-2': { x: 47.8, y: 53.0 },
    // Middle East
    'me-central-1': { x: 61.5, y: 59.5 },
    'me-south-1': { x: 60.2, y: 58.5 },
    'il-central-1': { x: 57.2, y: 56.2 },
    // Asia Pacific - South Asia
    'ap-south-1': { x: 67.0, y: 60.5 },
    'ap-south-2': { x: 69.0, y: 61.5 },
    // Asia Pacific - Southeast
    'ap-southeast-1': { x: 75.5, y: 65.5 },
    'ap-southeast-2': { x: 88.5, y: 84.3 },
    'ap-southeast-3': { x: 76.5, y: 68.0 },
    'ap-southeast-4': { x: 87.8, y: 82.5 },
    // Asia Pacific - East Asia
    'ap-northeast-1': { x: 84.5, y: 55.5 },
    'ap-northeast-2': { x: 81.8, y: 54.8 },
    'ap-northeast-3': { x: 83.5, y: 56.2 },
    'ap-east-1': { x: 79.5, y: 60.5 },
    // South America
    'sa-east-1': { x: 32.5, y: 72.5 },
    // Africa
    'af-south-1': { x: 52.2, y: 81.5 }
  };
  return coords[region];
};

const DashboardNew = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    security: null,
    costs: null,
    resources: null,
    accounts: [],
    tenantName: localStorage.getItem('tenantName') || '',
    tenantID: localStorage.getItem('tenantID') || localStorage.getItem('organizationID') || ''
  });
  const navigate = useNavigate();
  
  // Company user detection
  const companyID = localStorage.getItem('companyID');
  const userType = localStorage.getItem('userType');
  const isCompanyUser = userType === 'company' && companyID;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const tenantID = localStorage.getItem('tenantID') || localStorage.getItem('organizationID');
      const companyID = localStorage.getItem('companyID');
      const userType = localStorage.getItem('userType');
      
      // For company users, use companyID; for tenant users, use tenantID
      const isCompanyUser = userType === 'company' && companyID;
      const effectiveID = isCompanyUser ? companyID : tenantID;
      
      if (!effectiveID) {
        // Only redirect tenant users to connected accounts
        if (!isCompanyUser) {
          navigate('/dashboard/connected-accounts');
        }
        return;
      }

      // BACKWARD COMPATIBLE: Determine which ID to use
      const idParam = isCompanyUser ? `companyID=${companyID}` : `tenantID=${effectiveID}`;

      // Fetch company config for company users
      let userData = { tenantName: 'Company Portal' };
      if (isCompanyUser) {
        const companyRes = await fetch(
          `https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/msp/companies/${companyID}/config?companyID=${companyID}`
        );
        if (!companyRes.ok) throw new Error('Failed to fetch company data');
        userData = await companyRes.json();
      } else {
        const userRes = await fetch(
          `https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/tenant/settings?tenantID=${effectiveID}`
        );
        if (!userRes.ok) throw new Error('Failed to fetch user data');
        userData = await userRes.json();
      }

      // Fetch Security Trends (V2 API with tenantID or companyID)
      const securityTrendsUrl = process.env.REACT_APP_SECURITY_TRENDS_V2 || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/security/trends';
      const securityBody = isCompanyUser ? { companyID } : { tenantID: effectiveID };
      console.log('Security API call:', securityTrendsUrl, 'Body:', securityBody);
      const securityRes = await fetch(
        securityTrendsUrl,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(securityBody)
        }
      );

      // Fetch FinOps Dashboard (V2 API with tenantID or companyID)
      const finopsDashboardUrl = process.env.REACT_APP_FINOPS_DASHBOARD_V2 || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/dashboard';
      const finopsParam = isCompanyUser ? `companyID=${companyID}` : `tenantID=${effectiveID}`;
      console.log('FinOps API call:', `${finopsDashboardUrl}?${finopsParam}`);
      const finopsRes = await fetch(
        `${finopsDashboardUrl}?${finopsParam}`,
        { method: 'GET' }
      );

      // Fetch CloudMap Summary (V2 API with tenantID or companyID)
      const cloudmapApiUrl = process.env.REACT_APP_CLOUDMAP_API || 'https://9cdf5nf6ya.execute-api.us-west-2.amazonaws.com/prod';
      const cloudmapParam = isCompanyUser ? `companyID=${companyID}` : `tenantID=${effectiveID}`;
      console.log('CloudMap API call:', `${cloudmapApiUrl}/summary?${cloudmapParam}`);
      const cloudmapRes = await fetch(
        `${cloudmapApiUrl}/summary?${cloudmapParam}`,
        { method: 'GET' }
      );

      // REMOVED: CloudMap Insights - Takes 25 seconds, not used in dashboard

      const [securityData, finopsData, cloudmapData] = await Promise.all([
        securityRes.ok ? securityRes.json().catch(e => { console.error('Security JSON parse error:', e); return null; }) : null,
        finopsRes.ok ? finopsRes.json().catch(e => { console.error('FinOps JSON parse error:', e); return null; }) : null,
        cloudmapRes.ok ? cloudmapRes.json().catch(e => { console.error('CloudMap JSON parse error:', e); return null; }) : null
      ]);
      
      // Log API responses for debugging
      console.log('API Responses:', {
        security: { ok: securityRes.ok, status: securityRes.status, data: securityData },
        finops: { ok: finopsRes.ok, status: finopsRes.status, data: finopsData },
        cloudmap: { ok: cloudmapRes.ok, status: cloudmapRes.status, data: cloudmapData }
      });
      
      // Use regionSummary from Summary API (already has region data!)
      if (cloudmapData?.regionSummary) {
        cloudmapData.resourcesByRegion = cloudmapData.regionSummary;
      }
      
      // Parse organizations from user data (only for tenant users)
      let organizations = [];
      let accounts = [];
      if (!isCompanyUser && userData?.cloudAccounts) {
        const cloudAccountsData = typeof userData.cloudAccounts === 'string' 
          ? JSON.parse(userData.cloudAccounts) 
          : userData.cloudAccounts;
        organizations = cloudAccountsData.organizations || [];
        accounts = cloudAccountsData.aws || [];
      }

      setDashboardData({
        security: securityData,
        costs: finopsData,
        resources: cloudmapData,
        accounts: accounts,
        organizations: organizations,
        tenantName: isCompanyUser 
          ? (userData?.companyName || 'Company Portal')
          : (userData?.tenantName || 'My Organization'),
        tenantID: tenantID,
        // Store company config for account count calculation
        companyConfig: isCompanyUser ? userData : null
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [loadingText, setLoadingText] = useState('Loading dashboard...');

  useEffect(() => {
    if (loading) {
      const messages = [
        'Loading dashboard...',
        'Fetching security data...',
        'Analyzing cloud resources...',
        'Retrieving cost insights...',
        'Preparing your data...'
      ];
      let index = 0;
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingText(messages[index]);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="cloudmap-page container-fluid pb-5">
        <div className="container">
          {/* Show tenant/company info immediately while loading */}
          {(dashboardData.tenantID || companyID) && (
            <Row className="mb-3">
              <Col md={12}>
                <Card className="shadow-sm border-0" style={{background: 'linear-gradient(135deg, #342ead 0%, #5a52d5 100%)'}}>
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center text-white">
                      <FaServer size={40} className="me-3" />
                      <div>
                        <h5 className="mb-0">{dashboardData.tenantName || 'My Organization'}</h5>
                        <small style={{opacity: 0.9}}>
                          {userType === 'company' ? `Company ID: ${companyID}` : `Tenant ID: ${dashboardData.tenantID}`}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh' }}>
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <Alert variant="danger" className="m-4">
        Error loading dashboard: {error || 'No data available'}
      </Alert>
    );
  }

  const { security, costs, resources, insights, accounts, organizations, tenantName, tenantID } = dashboardData || {};

  // Don't render if logging out
  if (sessionStorage.getItem('isLoggingOut') === 'true') {
    return null;
  }

  // Don't render if no tenantID or companyID (user logged out)
  if (!dashboardData.tenantID && !isCompanyUser) {
    return null;
  }

  // Calculate metrics
  // Calculate security score based on severity (lower findings = higher score)
  const securityScore = security?.length > 0 && security[0]?.severityDistribution
    ? (() => {
        const dist = security[0].severityDistribution;
        const total = (dist.Critical || 0) + (dist.High || 0) + (dist.Medium || 0) + (dist.Low || 0);
        if (total === 0) return 100;
        // Weight: Critical=4, High=3, Medium=2, Low=1
        const weighted = (dist.Critical || 0) * 4 + (dist.High || 0) * 3 + (dist.Medium || 0) * 2 + (dist.Low || 0) * 1;
        const maxWeighted = total * 4;
        return Math.max(0, Math.round(100 - (weighted / maxWeighted) * 100));
      })()
    : 0;
  
  const totalCost = costs?.cost?.total || 0;
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const totalResources = resources?.totalResources || 0;
  
  // Calculate total connected accounts properly
  let totalConnectedAccounts = 0;
  
  if (isCompanyUser) {
    // For company users, use accountCount from MSP company config API (authoritative source)
    totalConnectedAccounts = dashboardData.companyConfig?.accountCount || 0;
  } else {
    // For tenant users, calculate from organizations and standalone accounts
    const singleAccounts = Array.isArray(accounts) ? accounts.length : 0;
    const orgAccounts = Array.isArray(organizations) 
      ? organizations.reduce((sum, org) => {
          const managementAccount = 1; // Each org has 1 management account
          const connectedMembers = org.memberAccounts?.filter(m => m.connected).length || 0;
          return sum + managementAccount + connectedMembers;
        }, 0)
      : 0;
    totalConnectedAccounts = singleAccounts + orgAccounts;
  }

  // Severity data
  const severityData = security?.[0]?.severityDistribution 
    ? (typeof security[0].severityDistribution === 'string' 
        ? JSON.parse(security[0].severityDistribution) 
        : security[0].severityDistribution)
    : { Critical: 0, High: 0, Medium: 0, Low: 0 };

  // Top 5 services by cost (exclude Savings Plans as they're already factored into total)
  const topServices = (costs?.cost?.byService || [])
    .filter(s => !s.service?.includes('Savings Plans'))
    .slice(0, 5);
  
  // Top resource types (exclude IAM roles and users - they're free)
  const topResourceTypes = (resources?.resourceTypes || [])
    .filter(r => !r.type?.includes('iam-roles') && !r.type?.includes('iam-users'))
    .slice(0, 5);



  return (
    <div className="cloudmap-page container-fluid pb-5" style={{ transition: 'margin-left 0.3s ease', marginLeft: isSidebarOpen ? '0px' : '0' }} key={`dashboard-${isSidebarOpen}`}>
      <div className="container">
      {/* Tenant/Company Info */}
      <Row className="mb-3">
        <Col md={12}>
          <Card className="shadow-sm border-0" style={{background: 'linear-gradient(135deg, #342ead 0%, #5a52d5 100%)'}}>
            <Card.Body className="py-3">
              <div className="d-flex align-items-center text-white">
                <FaServer size={40} className="me-3" />
                <div>
                  <h5 className="mb-0">{tenantName}</h5>
                  <small style={{opacity: 0.9}}>
                    {isCompanyUser ? `Company ID: ${companyID}` : `Tenant ID: ${tenantID}`}
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Key Metrics */}
        <Row className="g-4 mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Security Score</p>
                  <h3 className="mb-0">{securityScore}%</h3>
                </div>
                <FaShieldAlt size={40} color="#28a745" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">{currentMonth} MTD</p>
                  <h3 className="mb-0">${totalCost.toFixed(2)}</h3>
                </div>
                <FaDollarSign size={40} color="#ffc107" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Connected Accounts</p>
                  <h3 className="mb-0">{totalConnectedAccounts}</h3>
                </div>
                <FaServer size={40} color="#6D5BFF" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 - Cost Trend + Top Services (Left) & Cloud Resources Map (Right) */}
      <Row className="g-4 mb-4">
        {/* Left Column - Cost Trend + Top Services */}
        <Col md={6}>
          {/* Cost Trend Chart */}
          {costs?.trend?.months && Array.isArray(costs.trend.months) && costs.trend.months.length > 0 && (
            <Card className="shadow-sm border-0 mb-3">
              <Card.Body>
                <h5 className="mb-3">Cost Trend</h5>
                <Chart
                  type="bar"
                  series={[{ name: 'Total Cost', data: costs.trend.months.slice(-3).map(m => m?.total || 0) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 4, dataLabels: { position: 'top' } } },
                    dataLabels: {
                      enabled: true,
                      formatter: (val) => `$${(val/1000).toFixed(1)}K`,
                      offsetY: -20,
                      style: { fontSize: '12px', colors: ['#304758'] }
                    },
                    xaxis: {
                      categories: (costs.trend.months || []).slice(-3).map(m => {
                        if (!m || !m.month) return '';
                        const parts = String(m.month).split('-');
                        if (parts.length !== 2) return String(m.month);
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        const monthIndex = parseInt(parts[1]) - 1;
                        return (monthNames[monthIndex] || '') + ' ' + (parts[0] || '');
                      }),
                      labels: { style: { fontSize: '12px' } }
                    },
                    yaxis: { labels: { formatter: (val) => `$${(val/1000).toFixed(0)}K` } },
                    colors: ['#342ead'],
                    tooltip: { y: { formatter: (val) => `$${val.toLocaleString()}` } }
                  }}
                  height={200}
                />
              </Card.Body>
            </Card>
          )}

          {/* Top 5 Cost Services */}
          {costs && topServices.length > 0 && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5 className="mb-3">Top 5 Cost Services (Monthly: ${totalCost.toFixed(2)})</h5>
                <small className="text-muted d-block mb-2">* Excludes Savings Plans (already applied to total cost)</small>
                <Chart
                  type="bar"
                  series={[{ name: 'Cost ($)', data: topServices.length > 0 ? topServices.map(s => parseFloat(s.cost).toFixed(2)) : [] }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                    xaxis: { 
                      labels: { formatter: (val) => '$' + val },
                      categories: topServices.length > 0 ? topServices.map(s => s.service || 'Unknown') : []
                    },
                    yaxis: {
                      labels: { 
                        style: { fontSize: '11px' }
                      }
                    },
                    dataLabels: { 
                      enabled: true,
                      formatter: (val) => '$' + parseFloat(val).toFixed(2),
                      style: { fontSize: '10px' }
                    },
                    colors: ['#342ead']
                  }}
                  height={220}
                />
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* Right Column - Cloud Resources Map */}
        {resources && (
          <Col md={6}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Cloud Resources</h5>
                </div>
                <div style={{ position: 'relative', width: '100%', paddingBottom: '66%', background: '#f8f9fa', borderRadius: '8px' }}>
                  <img 
                    src="/world.svg" 
                    alt="World Map" 
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      opacity: 0.3,
                      filter: 'grayscale(100%)'
                    }} 
                  />
                  {resources?.resourcesByRegion && Object.keys(resources.resourcesByRegion).length > 0 && Object.entries(resources.resourcesByRegion).map(([region, count]) => {
                    const coords = getRegionCoords(region);
                    if (!coords) return null;
                    const regionCounts = Object.values(resources.resourcesByRegion).filter(c => typeof c === 'number' && c > 0);
                    const maxCount = regionCounts.length > 0 ? Math.max(...regionCounts) : 1;
                    const radius = 6 + (count / maxCount) * 18;
                    return (
                      <div
                        key={region}
                        style={{
                          position: 'absolute',
                          left: `${coords.x}%`,
                          top: `${coords.y}%`,
                          transform: 'translate(-50%, -50%)',
                          width: `${radius * 2}px`,
                          height: `${radius * 2}px`,
                          borderRadius: '50%',
                          background: '#6D5BFF',
                          opacity: 0.8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: radius > 12 ? '10px' : '8px',
                          fontWeight: '600',
                          boxShadow: '0 0 15px rgba(109, 91, 255, 0.4)',
                          cursor: 'pointer',
                          zIndex: 10
                        }}
                        title={`${region}: ${count} resources`}
                      >
                        {count}
                      </div>
                    );
                  })}
                </div>
                <div className="row text-center mt-3">
                  <div className="col-6">
                    <small className="text-muted">Total Resources</small>
                    <div className="fw-bold">{resources.totalResources || 0}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Regions</small>
                    <div className="fw-bold">{Object.keys(resources.resourcesByRegion || {}).length}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Charts Row 2 - Security & Resource Types */}
      {resources && topResourceTypes.length > 0 && (
      <Row className="g-4 mb-4">
        <Col md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h5 className="mb-3">Security Findings by Severity</h5>
              <Chart
                type="donut"
                series={[severityData.Critical, severityData.High, severityData.Medium, severityData.Low]}
                options={{
                  labels: ['Critical', 'High', 'Medium', 'Low'],
                  colors: ['#dc3545', '#fd7e14', '#ffc107', '#17a2b8'],
                  legend: { position: 'bottom' },
                  dataLabels: { enabled: true }
                }}
                height={300}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <h5 className="mb-3">Top 5 Resource Types</h5>
              <Chart
                type="bar"
                series={[{ name: 'Count', data: topResourceTypes.length > 0 ? topResourceTypes.map(r => r.count) : [] }]}
                options={{
                  chart: { toolbar: { show: false } },
                  xaxis: { 
                    categories: topResourceTypes.length > 0 ? topResourceTypes.map(r => r.displayName) : [],
                    labels: { rotate: -45, style: { fontSize: '11px' } }
                  },
                  colors: ['#17a2b8'],
                  dataLabels: { enabled: false }
                }}
                height={300}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      )}
      </div>
    </div>
  );
};

export default DashboardNew;
